/**
 * DeleteConfirmationDialog Component
 * Reusable confirmation dialog for deleting/clearing Model Planner items and sections
 */

import React, { useEffect, useRef } from 'react';
import { AlertTriangle } from 'lucide-react';

const DeleteConfirmationDialog = ({
  isOpen,
  itemName,
  itemType, // 'pond', 'wall', 'section', etc.
  onConfirm,
  onCancel,
  cascadeInfo = null // { count: 5, itemType: 'ADA ramps' }
}) => {
  const cancelButtonRef = useRef(null);

  useEffect(() => {
    if (isOpen && cancelButtonRef.current) {
      cancelButtonRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onCancel();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onCancel]);

  if (!isOpen) return null;

  const getTitle = () => {
    if (itemType === 'section') {
      return `Clear ${itemName}?`;
    }
    return `Delete ${itemName}?`;
  };

  const getMessage = () => {
    if (itemType === 'section') {
      return `Are you sure you want to clear all data from the ${itemName} section? This action cannot be undone.`;
    }
    return `Are you sure you want to delete "${itemName}"? This action cannot be undone.`;
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.75)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000
      }}
      onClick={onCancel}
    >
      <div
        style={{
          backgroundColor: 'var(--dark-surface)',
          borderRadius: 'var(--radius-lg)',
          padding: 'var(--space-6)',
          maxWidth: '500px',
          width: '90%',
          border: '1px solid var(--gray-700)',
          boxShadow: '0 10px 40px rgba(0, 0, 0, 0.5)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header with warning icon */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', marginBottom: 'var(--space-4)' }}>
          <AlertTriangle size={24} color="#f59e0b" />
          <h3 style={{ margin: 0, fontSize: 'var(--text-xl)', fontWeight: 'var(--font-semibold)', color: 'var(--gray-50)' }}>
            {getTitle()}
          </h3>
        </div>

        {/* Message */}
        <p style={{ margin: '0 0 var(--space-4) 0', fontSize: 'var(--text-base)', color: 'var(--gray-300)', lineHeight: '1.5' }}>
          {getMessage()}
        </p>

        {/* Cascade warning */}
        {cascadeInfo && (
          <div style={{
            padding: 'var(--space-3)',
            backgroundColor: 'rgba(245, 158, 11, 0.1)',
            border: '1px solid rgba(245, 158, 11, 0.3)',
            borderRadius: 'var(--radius-md)',
            marginBottom: 'var(--space-4)'
          }}>
            <p style={{ margin: 0, fontSize: 'var(--text-sm)', color: '#fbbf24' }}>
              <strong>Warning:</strong> This will also delete {cascadeInfo.count} {cascadeInfo.itemType}.
            </p>
          </div>
        )}

        {/* Action buttons */}
        <div style={{ display: 'flex', gap: 'var(--space-3)', justifyContent: 'flex-end' }}>
          <button
            ref={cancelButtonRef}
            onClick={onCancel}
            style={{
              padding: 'var(--space-2) var(--space-6)',
              fontSize: 'var(--text-base)',
              fontWeight: 'var(--font-medium)',
              border: '1px solid var(--gray-600)',
              backgroundColor: 'var(--dark-elevated)',
              color: 'var(--gray-200)',
              cursor: 'pointer',
              borderRadius: 'var(--radius-md)',
              transition: 'all var(--transition-fast)'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = 'var(--gray-700)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'var(--dark-elevated)';
            }}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            style={{
              padding: 'var(--space-2) var(--space-6)',
              fontSize: 'var(--text-base)',
              fontWeight: 'var(--font-semibold)',
              border: '1px solid #dc2626',
              backgroundColor: '#dc2626',
              color: 'white',
              cursor: 'pointer',
              borderRadius: 'var(--radius-md)',
              transition: 'all var(--transition-fast)'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#b91c1c';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = '#dc2626';
            }}
          >
            {itemType === 'section' ? 'Clear Section' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DeleteConfirmationDialog;
